import commonUtils from '@kathondvla/sri-client/common-utils';
import {
  getActivityBatch,
  getAttachmentBatch,
  getCreatePlanBatch,
} from '@store/llinkidApis/llinkidApiHelper';
import { generateCalendarWeeksForPlans } from '@utils/dateHelpers';
import { addMilliseconds } from 'date-fns/addMilliseconds';
import { addWeeks } from 'date-fns/addWeeks';
import { isSameDay } from 'date-fns/isSameDay';
import { createWeek } from '../../module/models/week';
import type { HrefObject } from '../../types/genericApiTypes';
import type { BatchItem, BatchRequest } from '../../types/sriTypes';
import type { ApiActivity, ApiActivityPlan } from '../../types/llinkidApiTypes';
import type {
  CalendarListFilters,
  PlanListItem,
  Week,
  GetExtraWeeksParams,
  CopyCalendarParams,
  OriginalCalendarForCopy,
  GoalTranslationsMap,
  ClassesMap,
  AttachmentsToUpload,
  CopyActivityResult,
  PlanCreatorsAndObservers,
  OrgsTeacherMap,
} from './calendarListTypes';

function getExtraWeeks({ week, amount, type }: GetExtraWeeksParams): Week[] {
  const { id, startDate, endDate } = week;
  const extraWeeks: Week[] = [];

  for (let i = 1; i <= amount; i += 1) {
    let nId: number;
    let nStartDate: Date;
    let nEndDate: Date;

    if (type === 'PREVIOUS') {
      nId = id - i;
      nStartDate = addWeeks(startDate, -i);
      nEndDate = addWeeks(endDate, -i);
    } else if (type === 'NEXT') {
      nId = id + i;
      nStartDate = addWeeks(startDate, i);
      nEndDate = addWeeks(endDate, i);
    } else {
      continue;
    }

    const newWeek = createWeek(nId, { start: nStartDate, end: nEndDate });

    extraWeeks.push(newWeek);
  }

  return extraWeeks;
}

function generateCopiedWeeks(copy: CopyCalendarParams, originalWeeks: Week[]): Week[] {
  let copiedWeeks = generateCalendarWeeksForPlans([
    {
      issued: {
        startDate: copy.schoolyear.startDate,
        endDate: copy.schoolyear.endDate,
      },
      activities: [],
    } as any,
  ]);

  if (originalWeeks[0].id < 1) {
    const previousWeeks = getExtraWeeks({
      week: copiedWeeks[0],
      amount: (originalWeeks[0].id - copiedWeeks[0].id) * -1,
      type: 'PREVIOUS',
    });
    copiedWeeks = [...previousWeeks.reverse(), ...copiedWeeks];
  }

  if (originalWeeks.length > copiedWeeks.length) {
    const nextWeeks = getExtraWeeks({
      week: copiedWeeks[copiedWeeks.length - 1],
      amount: originalWeeks.length - copiedWeeks.length,
      type: 'NEXT',
    });
    copiedWeeks = [...copiedWeeks, ...nextWeeks];
  }

  return copiedWeeks;
}

export function getGoalsToCopy(
  goals: HrefObject[] = [],
  goalTranslationsMap: GoalTranslationsMap = {},
): HrefObject[] {
  const goalsWithNextVersion = new Set(goals.map((g) => goalTranslationsMap[g.href] || g.href));

  return [...goalsWithNextVersion].map((href) => {
    return { href };
  });
}

function generateCopyActivity(
  activity: ApiActivity,
  originalWeeks: Week[],
  copiedWeeks: Week[],
  goalTranslationsMap: GoalTranslationsMap,
): CopyActivityResult {
  const key = commonUtils.generateUUID();
  const startIndex = originalWeeks.findIndex((w) =>
    isSameDay(w.startDate, new Date(activity.period.startDate)),
  );
  const endIndex = originalWeeks.findIndex((w) =>
    isSameDay(addMilliseconds(w.endDate, -1), new Date(activity.period.endDate)),
  );
  const period = {
    startDate: copiedWeeks[startIndex].startDate,
    endDate: addMilliseconds(copiedWeeks[endIndex].endDate, -1),
  };

  const goals = getGoalsToCopy(activity.goals, goalTranslationsMap);

  return { key, item: { ...activity, goals }, period };
}

export const getCopyCalendarBatch = ({
  original,
  copy,
  classesMap,
  goalTranslationsMap,
}: {
  original: OriginalCalendarForCopy;
  copy: CopyCalendarParams;
  classesMap: ClassesMap;
  goalTranslationsMap: GoalTranslationsMap;
}): [BatchRequest, AttachmentsToUpload] => {
  const planBatch = getCreatePlanBatch(copy) as BatchRequest;
  const batch = planBatch;

  const originalWeeks = generateCalendarWeeksForPlans(original.plans);
  const copiedWeeks = generateCopiedWeeks(copy, originalWeeks);
  const attachmentsToUpload: AttachmentsToUpload = {
    items: [],
    files: [],
  };

  Object.keys(classesMap).forEach((orginalClass) => {
    const originalPlan = original.plans.find((p) => p.class.href.includes(orginalClass));
    if (!originalPlan) return;

    const parents = originalPlan.activities.filter((a) => a.parent.href.includes(originalPlan.key));

    classesMap[orginalClass]?.forEach((newClass) => {
      const plan = planBatch.find((p: any) => p.body.class?.href.includes(newClass.key));
      if (!plan) return;

      parents.forEach((parent) => {
        const { key, item, period } = generateCopyActivity(
          parent,
          originalWeeks,
          copiedWeeks,
          goalTranslationsMap,
        );
        batch.push(
          getActivityBatch({
            key,
            parentHref: plan.href,
            activity: item,
            period,
          }) as BatchItem<'PUT'>,
        );

        originalPlan.activities
          .filter((a) => a.parent.href.includes(parent.key))
          .forEach((child) => {
            const {
              key: childKey,
              item: childItem,
              period: childPeriod,
            } = generateCopyActivity(child, originalWeeks, copiedWeeks, goalTranslationsMap);

            if (child.$$attachments) {
              child.$$attachments.forEach((att) => {
                attachmentsToUpload.items.push(
                  getAttachmentBatch({ activityKey: childKey, attachment: att.$$expanded }),
                );
              });
            }

            batch.push(
              getActivityBatch({
                key: childKey,
                parentHref: `/llinkid/activityplanning/activityplans/activities/${key}`,
                activity: childItem,
                period: childPeriod,
              }) as BatchItem<'PUT'>,
            );
          });
      });
    });
  });

  return [planBatch, attachmentsToUpload];
};

export const getPlanCreatorsAndObservers = (
  plan: ApiActivityPlan,
  orgsTeacherMap: OrgsTeacherMap,
): PlanCreatorsAndObservers => {
  const creators = new Set<string>();
  plan.creators.forEach((creator) => {
    if (orgsTeacherMap[creator.href]) {
      orgsTeacherMap[creator.href].forEach((href) => {
        creators.add(href);
      });
    }
  });
  const $$creatorsSet = creators; // /we use the set internally
  const $$creators = [...creators]; // /we use an array externally, because a set doesn't clone with JSON

  const observers = new Set<string>();
  plan.observers?.forEach((creator) => {
    if (orgsTeacherMap[creator.href]) {
      orgsTeacherMap[creator.href].forEach((href) => {
        observers.add(href);
      });
    }
  });
  const $$observerSet = observers; // /we use the set internally
  const $$observers = [...observers]; // /we use an array externally, because a set doesn't clone with JSON

  const creatorsAndObserversForPlan: PlanCreatorsAndObservers = {
    $$creatorsSet,
    $$creators,
    $$observerSet,
    $$observers,
  };

  return creatorsAndObserversForPlan;
};

export const filterData = (filters: CalendarListFilters, list: PlanListItem[]): PlanListItem[] => {
  const { deletedCalendars, name } = filters;
  let filteredList = [...list];

  if (deletedCalendars) filteredList = filteredList.filter((e) => e.deleted);
  else filteredList = filteredList.filter((e) => !e.deleted);

  if (name) {
    filteredList = filteredList.filter((result) => {
      const regExp = new RegExp(name, 'gi');
      return (
        regExp.test(result.classes) || regExp.test(result.plan) || regExp.test(result.teachers)
      );
    });
  }

  return filteredList;
};

export const getArchiveCalendarBatch = (plans: ApiActivityPlan[]): BatchRequest => {
  const batch: BatchRequest = [];

  const timestamp = new Date().toISOString();

  plans.forEach((plan) => {
    batch.push({
      verb: 'PUT' as const,
      href: plan.$$meta.permalink,
      body: { ...plan, softDeleted: timestamp },
    });
  });

  return batch;
};

export const getRemoveCalendarBatch = (
  plans: ApiActivityPlan[],
  activities: ApiActivity[],
): BatchRequest => {
  const batch: BatchRequest = [];

  plans.forEach((plan) => {
    batch.push({
      verb: 'DELETE' as const,
      href: plan.$$meta.permalink,
      body: plan,
    });
  });

  activities.forEach((activity) => {
    batch.push({
      verb: 'DELETE' as const,
      href: activity.$$meta.permalink,
      body: activity,
    });
  });

  return batch;
};
